/**
 * CostEstimateCard
 * ----------------
 * The ONE shared estimate box used everywhere the platform previews a
 * cost before the user commits — pipeline creation, goal creation,
 * fine-tuning, etc. Guarantees the same look + language for billing
 * expectations across the app:
 *
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │ ⊞ {title}  ⓘ           [billing badges]        Explain ─────► │
 *   │                                                               │
 *   │  BIG NUMBER   │   BIG NUMBER   │   BIG NUMBER                 │
 *   │  label        │   label        │   label                      │
 *   │                                                               │
 *   │  balance / coverage footer            [action buttons]        │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * The card is purely presentational; callers own their data fetching
 * and pass display-ready numbers. The "Explain" dialog shell (title,
 * close button, footnote) is provided here; its body is caller JSX so
 * each flow can show its own breakdown, composed from the exported
 * `FactorRow` / `BreakdownRow` primitives.
 *
 * Billing badges (``billingModes``) are the transparency layer: a
 * compact chip row that tells the user WHAT KIND of billing to expect
 * (pre-charged per run, charged today, monthly recurring, usage-based)
 * before any number is read.
 */

import React from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Divider, Tooltip, useTheme, alpha,
  Skeleton, Alert, Button, Dialog, DialogTitle, DialogContent, IconButton, Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import DataUsageRoundedIcon from '@mui/icons-material/DataUsageRounded';

import { formatCredits } from './billingHelpers';

// ---------------------------------------------------------------------------
// Billing-mode badges — the shared vocabulary for "what kind of charge
// is this". Callers pass ids; the card renders consistent chips.
// ---------------------------------------------------------------------------

const BILLING_MODES = {
  charged_today: {
    label: 'Charged today',
    hint: 'A one-time amount is deducted from your balance when you confirm.',
    Icon: TodayRoundedIcon,
    tone: 'warning',
  },
  per_run: {
    label: 'Pre-charged per run',
    hint: 'Each run is charged when it starts. Failed runs are automatically refunded.',
    Icon: BoltRoundedIcon,
    tone: 'info',
  },
  monthly_recurring: {
    label: 'Monthly recurring',
    hint: 'An ongoing cost billed continuously while this stays active. Stop or pause it any time.',
    Icon: EventRepeatRoundedIcon,
    tone: 'secondary',
  },
  usage_based: {
    label: 'Usage-based',
    hint: 'Billed by actual usage (compute time, requests, or data volume) — the number shown is an estimate.',
    Icon: DataUsageRoundedIcon,
    tone: 'default',
  },
};

const BillingModeChips = ({ modes }) => {
  const theme = useTheme();
  if (!modes || modes.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {modes.map((id) => {
        const mode = BILLING_MODES[id];
        if (!mode) return null;
        const { Icon } = mode;
        const color = mode.tone === 'default'
          ? theme.palette.text.secondary
          : (theme.palette[mode.tone]?.main || theme.palette.primary.main);
        return (
          <Tooltip key={id} title={mode.hint} arrow placement="top">
            <Chip
              size="small"
              icon={<Icon sx={{ fontSize: 13 }} />}
              label={mode.label}
              sx={{
                height: 20,
                fontSize: '0.66rem',
                fontWeight: 600,
                cursor: 'help',
                color,
                bgcolor: alpha(color, 0.08),
                border: `1px solid ${alpha(color, 0.25)}`,
                '& .MuiChip-icon': { color },
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// CostStat — one big headline number. Shared by the card + explain dialog.
// ---------------------------------------------------------------------------

export const CostStat = ({ label, value, loading, error, accent, raw, suffix, unit = 'credits' }) => {
  const theme = useTheme();
  const formatted = (() => {
    if (loading) return null;
    if (error) return '—';
    if (value == null) return '—';
    if (raw) return Math.round(value).toLocaleString();
    return formatCredits(value, { compact: false });
  })();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={90} height={32} />
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: accent ? theme.palette.primary.main : 'text.primary',
              lineHeight: 1.1,
            }}
          >
            {suffix || ''}{formatted}
          </Typography>
          {!raw && formatted !== '—' && (
            <Typography variant="caption" color="text.secondary">{unit}</Typography>
          )}
        </Stack>
      )}
      {error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
          {String(error)}
        </Typography>
      )}
    </Box>
  );
};

const StatRow = ({ stats, mb = 2 }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={2.5}
    divider={(
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
    )}
    sx={{ mb }}
  >
    {stats.map((s) => <CostStat key={s.label} {...s} />)}
  </Stack>
);

// ---------------------------------------------------------------------------
// Explain-dialog row primitives
// ---------------------------------------------------------------------------

/** "label · value · affects-cost chip" row for cost-factor lists. */
export const FactorRow = ({ label, value, hint, affects }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
    <Typography variant="caption" sx={{ minWidth: 130, color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {typeof value === 'string' || typeof value === 'number'
        ? <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
        : value}
    </Box>
    <Tooltip title={hint || ''} arrow placement="top">
      <Chip
        size="small"
        variant="outlined"
        label={affects ? 'affects cost' : 'no impact'}
        sx={(theme) => ({
          height: 18,
          fontSize: '0.65rem',
          borderColor: affects
            ? alpha(theme.palette.primary.main, 0.4)
            : theme.palette.divider,
          color: affects ? 'primary.main' : 'text.secondary',
          backgroundColor: affects
            ? alpha(theme.palette.primary.main, 0.06)
            : 'transparent',
          cursor: 'help',
        })}
      />
    </Tooltip>
  </Stack>
);

/** "label + hint on the left, amount on the right" row for line-item breakdowns. */
export const BreakdownRow = ({ label, hint, value }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {hint}
        </Typography>
      )}
    </Box>
    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{value}</Typography>
  </Stack>
);

/** Uppercase section label inside the explain dialog. */
export const ExplainSectionTitle = ({ children, sx }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, ...sx }}
  >
    {children}
  </Typography>
);

// ---------------------------------------------------------------------------
// CoverageFooter — the standardized balance / coverage line.
//
// coverage: null (balance-only line) or
//   { tier: 'insufficient', title?, detail, }        -> red alert + Top up CTA
//   { tier: 'partial', chipLabel }                   -> amber chip + small Top up
//   { tier: 'ok', chipLabel }                        -> neutral chip
// ---------------------------------------------------------------------------

export const CoverageFooter = ({ balance, coverage }) => {
  const navigate = useNavigate();
  if (balance == null) return null;

  if (coverage?.tier === 'insufficient') {
    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRoundedIcon fontSize="small" />}
        sx={{ mt: 1.5, alignItems: 'center', '& .MuiAlert-message': { flex: 1 } }}
        action={(
          <Button
            size="small"
            variant="contained"
            color="warning"
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => navigate('/settings/billing')}
            sx={{ height: 30, whiteSpace: 'nowrap' }}
          >
            Top up
          </Button>
        )}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {coverage.title || 'Insufficient credits'}
        </Typography>
        {coverage.detail && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {coverage.detail}
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary">
        Your balance:&nbsp;
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {formatCredits(balance)} credits
        </Box>
      </Typography>
      {coverage?.tier === 'partial' && (
        <>
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={coverage.chipLabel}
            sx={{ height: 20 }}
          />
          <Button
            size="small"
            variant="text"
            startIcon={<AddCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/settings/billing')}
            sx={{ height: 24, py: 0, px: 1, fontSize: '0.72rem', minWidth: 0 }}
          >
            Top up
          </Button>
        </>
      )}
      {coverage?.tier === 'ok' && coverage.chipLabel && (
        <Chip size="small" variant="outlined" label={coverage.chipLabel} sx={{ height: 20 }} />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// The card itself
// ---------------------------------------------------------------------------

const CostEstimateCard = ({
  title = 'Cost estimator',
  infoTip,
  billingModes,          // array of BILLING_MODES ids, e.g. ['per_run', 'monthly_recurring']
  stats,                 // array of CostStat props
  subline,               // optional caption under the header (e.g. "+120 cr one-time today")
  emptyText,             // renders instead of stats when caller lacks inputs
  balance,               // number | null — feeds CoverageFooter
  coverage,              // see CoverageFooter
  footerRight,           // optional JSX (action buttons) bottom-right
  footerExtra,           // optional JSX rendered under the footer row (e.g. Alerts)
  explainTitle = 'Cost breakdown',
  explainContent,        // JSX body of the Explain dialog (rows via FactorRow/BreakdownRow)
  explainFootnote = 'All amounts are in credits. Storage is billed separately.',
  sx,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const [explainOpen, setExplainOpen] = React.useState(false);
  const hasStats = Array.isArray(stats) && stats.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderColor: alpha(accent, 0.35),
        bgcolor: alpha(accent, 0.04),
        borderRadius: 1.5,
        ...sx,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasStats ? 2 : 1, flexWrap: 'wrap', rowGap: 0.75 }}>
        <CalculateRoundedIcon sx={{ color: accent, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
          {title}
        </Typography>
        {infoTip && (
          <Tooltip title={infoTip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 15, color: 'text.secondary', opacity: 0.7, cursor: 'help' }}
            />
          </Tooltip>
        )}
        <BillingModeChips modes={billingModes} />
        {hasStats && explainContent && (
          <Link
            component="button"
            type="button"
            underline="hover"
            variant="caption"
            onClick={() => setExplainOpen(true)}
            sx={{ ml: 'auto', fontWeight: 600 }}
          >
            Explain
          </Link>
        )}
      </Stack>

      {subline && hasStats && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1.5, mb: 1.5 }}>
          {subline}
        </Typography>
      )}

      {!hasStats && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyText || 'Fill in the form above to see the estimated cost.'}
        </Typography>
      )}

      {hasStats && (
        <>
          <StatRow stats={stats} mb={footerRight || balance != null ? 2 : 0} />

          {/* Footer: coverage on the left, actions on the right. */}
          {(balance != null || footerRight) && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'flex-end' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CoverageFooter balance={balance} coverage={coverage} />
              </Box>
              {footerRight && (
                <Box sx={{ flexShrink: 0 }}>{footerRight}</Box>
              )}
            </Stack>
          )}
          {footerExtra}

          {/* Explain dialog shell */}
          <Dialog open={explainOpen} onClose={() => setExplainOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalculateRoundedIcon sx={{ color: accent, fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{explainTitle}</Typography>
              </Stack>
              <IconButton
                onClick={() => setExplainOpen(false)}
                size="small"
                sx={{ position: 'absolute', top: 12, right: 12 }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Headline numbers repeated for context */}
              <StatRow stats={stats} />
              {explainContent}
              {explainFootnote && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 2, fontSize: '0.68rem', opacity: 0.75, lineHeight: 1.4 }}
                >
                  {explainFootnote}
                </Typography>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Paper>
  );
};

export default CostEstimateCard;
